<template>
  <div v-if="canView && isSuperAdmin" class="ai-chat-settings-page">
    <card :title="$t('ai_assistant.settings_title')">
      <div class="ai-chat-settings-page__form">
        <div class="ai-chat-settings-page__field">
          <label class="ai-chat-settings-page__label" for="ai-chat-api-url">{{ $t("ai_assistant.settings_api_url") }}</label>
          <input
            id="ai-chat-api-url"
            v-model.trim="form.openrouter_api_url"
            type="text"
            class="ai-chat-settings-page__input"
            :disabled="loading || !canEdit"
          />
        </div>

        <div class="ai-chat-settings-page__field">
          <div class="ai-chat-settings-page__label-row">
            <label class="ai-chat-settings-page__label" for="ai-chat-model">{{ $t("ai_assistant.settings_model") }}</label>
            <DxButton
              styling-mode="outlined"
              type="normal"
              :text="modelsLoading ? $t('ai_assistant.loading_models') : $t('ai_assistant.refresh_models')"
              :disabled="loading || modelsLoading || !canEdit"
              @click="refreshModels"
            />
          </div>

          <div class="ai-chat-settings-page__grid-two">
            <select
              id="ai-chat-model"
              v-model="form.openrouter_model"
              class="ai-chat-settings-page__input"
              :disabled="loading || modelsLoading || modelGroups.length === 0 || !canEdit"
            >
              <option v-if="modelGroups.length === 0" value="">{{ $t("ai_assistant.no_models") }}</option>
              <optgroup v-for="group in modelGroups" :key="group.provider" :label="group.providerLabel">
                <option v-for="model in group.models" :key="model.id" :value="model.id">
                  {{ model.name }} ({{ model.id }})
                </option>
              </optgroup>
            </select>

            <select
              id="ai-chat-model-variant"
              v-model="form.model_variant"
              class="ai-chat-settings-page__input"
              :disabled="loading || !canEdit"
            >
              <option v-for="variant in modelVariantList" :key="`variant-${variant}`" :value="variant">
                {{ $t(`ai_assistant.settings_model_variant_${variant}`) }}
              </option>
            </select>
          </div>

          <div v-if="effectiveModelId" class="ai-chat-settings-page__small">
            {{ $t("ai_assistant.settings_model_effective") }}: {{ effectiveModelId }}
          </div>
          <div v-if="form.model_variant !== 'none'" class="ai-chat-settings-page__small">
            {{ $t(`ai_assistant.settings_model_variant_help_${form.model_variant}`) }}
          </div>

          <div v-if="selectedModel" class="ai-chat-settings-page__model-card">
            <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.provider") }}: {{ selectedModel.providerLabel }}</div>
            <div v-if="selectedModelPricingText" class="ai-chat-settings-page__small">{{ $t("ai_assistant.price") }}: {{ selectedModelPricingText }}</div>
          </div>

          <div v-if="modelsNoticeText" class="ai-chat-settings-page__notice">{{ modelsNoticeText }}</div>
        </div>

        <div class="ai-chat-settings-page__field">
          <label class="ai-chat-settings-page__label" for="ai-chat-api-key">{{ $t("ai_assistant.settings_api_key") }}</label>
          <input
            id="ai-chat-api-key"
            v-model.trim="form.openrouter_api_key"
            type="password"
            class="ai-chat-settings-page__input"
            :placeholder="apiKeyPlaceholder"
            :disabled="loading || !canEdit"
          />
          <div v-if="currentSettings.has_api_key" class="ai-chat-settings-page__small">
            {{ $t("ai_assistant.settings_api_key_saved") }}: {{ currentSettings.api_key_masked }}
          </div>
          <label v-if="canEdit" class="ai-chat-settings-page__checkbox">
            <input v-model="form.clear_api_key" type="checkbox" :disabled="loading" />
            <span>{{ $t("ai_assistant.settings_clear_api_key") }}</span>
          </label>
        </div>

        <div class="ai-chat-settings-page__field">
          <label class="ai-chat-settings-page__label" for="ai-chat-system-prompt">{{ $t("ai_assistant.settings_system_prompt") }}</label>
          <textarea
            id="ai-chat-system-prompt"
            v-model.trim="form.system_prompt"
            class="ai-chat-settings-page__textarea"
            :disabled="loading || !canEdit"
          />
          <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_system_prompt_help") }}</div>
        </div>

        <div class="ai-chat-settings-page__field">
          <label class="ai-chat-settings-page__label" for="ai-chat-initial-user-prompt">{{ $t("ai_assistant.settings_initial_user_prompt") }}</label>
          <textarea
            id="ai-chat-initial-user-prompt"
            v-model.trim="form.initial_user_prompt"
            class="ai-chat-settings-page__textarea"
            :disabled="loading || !canEdit"
          />
          <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_initial_user_prompt_help") }}</div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_tool_runtime_section") }}</div>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.tool_calls_enabled" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_tool_calls_enabled") }}</span>
          </label>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.persist_loaded_tools_in_session" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_persist_loaded_tools_session") }}</span>
          </label>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-mcp-api-max-limit">{{ $t("ai_assistant.settings_mcp_api_max_limit") }}</label>
            <input
              id="ai-chat-mcp-api-max-limit"
              v-model.number="form.mcp_api_max_limit"
              type="number"
              min="1"
              max="1000"
              step="1"
              class="ai-chat-settings-page__input"
              :disabled="loading || !canEdit"
            />
            <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_mcp_api_max_limit_help") }}</div>
          </div>

          <div class="ai-chat-settings-page__subsection-title">{{ $t("ai_assistant.settings_tool_method_confirmation_section") }}</div>
          <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_tool_method_confirmation_help") }}</div>
          <div class="ai-chat-settings-page__method-grid">
            <label v-for="method in toolMethodList" :key="method" class="ai-chat-settings-page__checkbox ai-chat-settings-page__method-item">
              <input
                type="checkbox"
                :checked="Boolean(form.tool_method_confirmation?.[method])"
                :disabled="loading || !canEdit || !form.tool_calls_enabled"
                @change="updateToolMethodConfirmation(method, $event.target.checked)"
              />
              <span>{{ method.toUpperCase() }}</span>
            </label>
          </div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_trace_visibility_section") }}</div>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.show_status_thinking" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_show_status_thinking") }}</span>
          </label>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.show_reasoning_thinking" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_show_reasoning_thinking") }}</span>
          </label>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.show_tool_activity" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_show_tool_activity") }}</span>
          </label>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.show_token_costs" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_show_token_costs") }}</span>
          </label>
          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.stream_reasoning_response" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_stream_reasoning_response") }}</span>
          </label>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_builtin_tools_section") }}</div>
          <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_builtin_tools_help") }}</div>

          <div class="ai-chat-settings-page__tool-list">
            <div class="ai-chat-settings-page__tool-header">
              <div>{{ $t("ai_assistant.settings_builtin_tool_name") }}</div>
              <div>{{ $t("ai_assistant.settings_builtin_tool_enabled") }}</div>
              <div>{{ $t("ai_assistant.settings_builtin_tool_always_send") }}</div>
            </div>

            <div
              v-for="tool in builtInToolsCatalog"
              :key="tool.name"
              class="ai-chat-settings-page__tool-row"
            >
              <div>
                <div class="ai-chat-settings-page__tool-name">{{ tool.title || tool.name }}</div>
                <div class="ai-chat-settings-page__small">{{ tool.description || tool.name }}</div>
              </div>
              <div>
                <input
                  type="checkbox"
                  :checked="Boolean(form.built_in_tools_config?.[tool.name]?.enabled)"
                  :disabled="loading || !canEdit"
                  @change="updateBuiltInToolFlag(tool.name, 'enabled', $event.target.checked)"
                />
              </div>
              <div>
                <input
                  type="checkbox"
                  :checked="Boolean(form.built_in_tools_config?.[tool.name]?.always_send)"
                  :disabled="loading || !canEdit || !Boolean(form.built_in_tools_config?.[tool.name]?.enabled)"
                  @change="updateBuiltInToolFlag(tool.name, 'always_send', $event.target.checked)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_assistant_runtime_section") }}</div>

          <div class="ai-chat-settings-page__grid-two">
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-assistant-temperature">{{ $t("ai_assistant.settings_assistant_temperature") }}</label>
              <input
                id="ai-chat-assistant-temperature"
                v-model.number="form.assistant_temperature"
                type="number"
                min="0"
                max="2"
                step="0.05"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit"
              />
            </div>
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-assistant-max-tokens">{{ $t("ai_assistant.settings_assistant_max_output_tokens") }}</label>
              <input
                id="ai-chat-assistant-max-tokens"
                v-model.number="form.assistant_max_output_tokens"
                type="number"
                min="1"
                max="65535"
                step="1"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit"
              />
            </div>
          </div>

          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.assistant_reasoning_enabled" type="checkbox" :disabled="loading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_assistant_reasoning_enabled") }}</span>
          </label>

          <div class="ai-chat-settings-page__grid-two">
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-assistant-reasoning-effort">{{ $t("ai_assistant.settings_reasoning_effort") }}</label>
              <select
                id="ai-chat-assistant-reasoning-effort"
                v-model="form.assistant_reasoning_effort"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit || !form.assistant_reasoning_enabled"
              >
                <option value="">{{ $t("common.select") }}</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
              </select>
            </div>
            <label class="ai-chat-settings-page__checkbox ai-chat-settings-page__checkbox--inline">
              <input v-model="form.assistant_include_reasoning" type="checkbox" :disabled="loading || !canEdit || !form.assistant_reasoning_enabled" />
              <span>{{ $t("ai_assistant.settings_include_reasoning") }}</span>
            </label>
          </div>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-assistant-model-params">{{ $t("ai_assistant.settings_assistant_model_params") }}</label>
            <textarea
              id="ai-chat-assistant-model-params"
              v-model.trim="form.assistant_model_params_json"
              class="ai-chat-settings-page__textarea"
              :disabled="loading || !canEdit"
              :placeholder="jsonPlaceholder"
            />
          </div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_router_runtime_section") }}</div>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-tool-loading-mode">{{ $t("ai_assistant.settings_tool_loading_mode") }}</label>
            <select
              id="ai-chat-tool-loading-mode"
              v-model="form.tool_loading_mode"
              class="ai-chat-settings-page__input"
              :disabled="loading || !canEdit"
            >
              <option value="router">{{ $t("ai_assistant.settings_tool_loading_mode_router") }}</option>
              <option value="all">{{ $t("ai_assistant.settings_tool_loading_mode_all") }}</option>
              <option value="openrouter_tool_search">{{ $t("ai_assistant.settings_tool_loading_mode_openrouter_tool_search") }}</option>
            </select>
            <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_tool_loading_mode_help") }}</div>
            <div v-if="form.tool_loading_mode === 'openrouter_tool_search'" class="ai-chat-settings-page__notice">
              {{ $t("ai_assistant.settings_tool_loading_mode_openrouter_tool_search_help") }}
            </div>
          </div>

          <div v-if="form.tool_loading_mode === 'openrouter_tool_search'" class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-tool-search-max-results">{{ $t("ai_assistant.settings_tool_search_max_results") }}</label>
            <input
              id="ai-chat-tool-search-max-results"
              v-model.number="form.openrouter_tool_search_max_results"
              type="number"
              min="1"
              max="50"
              step="1"
              class="ai-chat-settings-page__input"
              :disabled="loading || !canEdit"
            />
            <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_tool_search_max_results_help") }}</div>
          </div>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-router-model">{{ $t("ai_assistant.settings_router_model") }}</label>
            <div class="ai-chat-settings-page__grid-two">
              <select
                id="ai-chat-router-model"
                v-model="form.router_model"
                class="ai-chat-settings-page__input"
                :disabled="loading || modelsLoading || modelGroups.length === 0 || !canEdit || form.tool_loading_mode !== 'router'"
              >
                <option v-if="modelGroups.length === 0" value="">{{ $t("ai_assistant.no_models") }}</option>
                <optgroup v-for="group in modelGroups" :key="`router-${group.provider}`" :label="group.providerLabel">
                  <option v-for="model in group.models" :key="`router-${model.id}`" :value="model.id">
                    {{ model.name }} ({{ model.id }})
                  </option>
                </optgroup>
              </select>

              <select
                id="ai-chat-router-model-variant"
                v-model="form.model_variant"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit || form.tool_loading_mode !== 'router'"
              >
                <option v-for="variant in modelVariantList" :key="`router-variant-${variant}`" :value="variant">
                  {{ $t(`ai_assistant.settings_model_variant_${variant}`) }}
                </option>
              </select>
            </div>
            <div v-if="effectiveRouterModelId" class="ai-chat-settings-page__small">
              {{ $t("ai_assistant.settings_model_effective") }}: {{ effectiveRouterModelId }}
            </div>
          </div>

          <div class="ai-chat-settings-page__grid-two">
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-router-temperature">{{ $t("ai_assistant.settings_router_temperature") }}</label>
              <input
                id="ai-chat-router-temperature"
                v-model.number="form.router_temperature"
                type="number"
                min="0"
                max="2"
                step="0.05"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit || form.tool_loading_mode !== 'router'"
              />
            </div>
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-router-max-tokens">{{ $t("ai_assistant.settings_router_max_output_tokens") }}</label>
              <input
                id="ai-chat-router-max-tokens"
                v-model.number="form.router_max_output_tokens"
                type="number"
                min="1"
                max="65535"
                step="1"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit || form.tool_loading_mode !== 'router'"
              />
            </div>
          </div>

          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.router_reasoning_enabled" type="checkbox" :disabled="loading || !canEdit || form.tool_loading_mode !== 'router'" />
            <span>{{ $t("ai_assistant.settings_router_reasoning_enabled") }}</span>
          </label>

          <div class="ai-chat-settings-page__grid-two">
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-chat-router-reasoning-effort">{{ $t("ai_assistant.settings_reasoning_effort") }}</label>
              <select
                id="ai-chat-router-reasoning-effort"
                v-model="form.router_reasoning_effort"
                class="ai-chat-settings-page__input"
                :disabled="loading || !canEdit || form.tool_loading_mode !== 'router' || !form.router_reasoning_enabled"
              >
                <option value="">{{ $t("common.select") }}</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
              </select>
            </div>
            <label class="ai-chat-settings-page__checkbox ai-chat-settings-page__checkbox--inline">
              <input v-model="form.router_include_reasoning" type="checkbox" :disabled="loading || !canEdit || form.tool_loading_mode !== 'router' || !form.router_reasoning_enabled" />
              <span>{{ $t("ai_assistant.settings_include_reasoning") }}</span>
            </label>
          </div>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-router-model-params">{{ $t("ai_assistant.settings_router_model_params") }}</label>
            <textarea
              id="ai-chat-router-model-params"
              v-model.trim="form.router_model_params_json"
              class="ai-chat-settings-page__textarea"
              :disabled="loading || !canEdit || form.tool_loading_mode !== 'router'"
              :placeholder="jsonPlaceholder"
            />
          </div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.settings_openrouter_cache_section") }}</div>

          <label class="ai-chat-settings-page__checkbox">
            <input v-model="form.openrouter_response_cache_enabled" type="checkbox" :disabled="loading || cacheResetLoading || !canEdit" />
            <span>{{ $t("ai_assistant.settings_openrouter_cache_enabled") }}</span>
          </label>

          <div class="ai-chat-settings-page__field">
            <label class="ai-chat-settings-page__label" for="ai-chat-openrouter-cache-ttl">{{ $t("ai_assistant.settings_openrouter_cache_ttl_seconds") }}</label>
            <input
              id="ai-chat-openrouter-cache-ttl"
              v-model.number="form.openrouter_response_cache_ttl_seconds"
              type="number"
              min="1"
              max="86400"
              step="1"
              class="ai-chat-settings-page__input"
              :disabled="loading || cacheResetLoading || !canEdit || !form.openrouter_response_cache_enabled"
            />
            <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.settings_openrouter_cache_ttl_help") }}</div>
          </div>

          <div class="ai-chat-settings-page__actions">
            <DxButton
              styling-mode="outlined"
              type="danger"
              :text="cacheResetLoading ? $t('ai_assistant.settings_openrouter_cache_resetting') : $t('ai_assistant.settings_openrouter_cache_reset_button')"
              :disabled="loading || cacheResetLoading || !canEdit"
              @click="resetCache"
            />
          </div>
        </div>

        <div class="ai-chat-settings-page__section">
          <div class="ai-chat-settings-page__section-title">{{ $t("ai_assistant.knowledge_title") }}</div>
          <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.knowledge_help") }}</div>

          <div class="ai-chat-settings-page__actions">
            <DxButton
              styling-mode="outlined"
              type="normal"
              :text="$t('ai_assistant.knowledge_export_all')"
              :disabled="knowledgeLoading || knowledgeItems.length === 0 || !canEdit"
              @click="exportKnowledge()"
            />
            <DxButton
              styling-mode="outlined"
              type="normal"
              :text="knowledgeImporting ? $t('ai_assistant.knowledge_importing') : $t('ai_assistant.knowledge_import')"
              :disabled="knowledgeLoading || knowledgeImporting || !canEdit"
              @click="$refs.knowledgeImportInput.click()"
            />
            <input
              ref="knowledgeImportInput"
              type="file"
              accept="application/json,.json"
              class="ai-chat-settings-page__file-input"
              @change="importKnowledgeFile"
            />
            <DxButton
              styling-mode="contained"
              type="default"
              :text="$t('ai_assistant.knowledge_new')"
              :disabled="knowledgeLoading || !canEdit"
              @click="startNewKnowledge"
            />
          </div>

          <div v-if="knowledgeEditorOpen" class="ai-chat-settings-page__knowledge-editor">
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-knowledge-title">{{ $t("ai_assistant.knowledge_item_title") }}</label>
              <input
                id="ai-knowledge-title"
                v-model.trim="knowledgeForm.title"
                type="text"
                maxlength="191"
                class="ai-chat-settings-page__input"
                :disabled="knowledgeSaving || !canEdit"
              />
            </div>
            <div class="ai-chat-settings-page__field">
              <label class="ai-chat-settings-page__label" for="ai-knowledge-description">{{ $t("ai_assistant.knowledge_description") }}</label>
              <textarea
                id="ai-knowledge-description"
                v-model="knowledgeForm.description"
                rows="8"
                class="ai-chat-settings-page__textarea"
                :disabled="knowledgeSaving || !canEdit"
              />
            </div>
            <div class="ai-chat-settings-page__actions">
              <DxButton
                styling-mode="contained"
                type="default"
                :text="knowledgeSaving ? $t('ai_assistant.knowledge_indexing') : $t('common.save')"
                :disabled="knowledgeSaving || !canEdit || !knowledgeForm.title || !knowledgeForm.description.trim()"
                @click="saveKnowledge"
              />
              <DxButton
                styling-mode="outlined"
                type="normal"
                :text="$t('common.cancel')"
                :disabled="knowledgeSaving"
                @click="cancelKnowledgeEdit"
              />
            </div>
          </div>

          <div v-if="knowledgeItems.length" class="ai-chat-settings-page__knowledge-list">
            <div v-for="item in knowledgeItems" :key="item.id" class="ai-chat-settings-page__knowledge-row">
              <div class="ai-chat-settings-page__knowledge-meta">
                <div class="ai-chat-settings-page__tool-name">{{ item.title }}</div>
                <div class="ai-chat-settings-page__small">{{ $t("ai_assistant.knowledge_pages", { count: item.page_count }) }}</div>
              </div>
              <div class="ai-chat-settings-page__actions">
                <DxButton styling-mode="outlined" type="normal" :text="$t('common.edit')" :disabled="knowledgeLoading || !canEdit" @click="editKnowledge(item)" />
                <DxButton styling-mode="outlined" type="normal" :text="$t('ai_assistant.knowledge_export_one')" :disabled="knowledgeLoading || !canEdit" @click="exportKnowledge(item.id)" />
                <DxButton styling-mode="outlined" type="danger" :text="$t('common.delete')" :disabled="knowledgeLoading || !canEdit" @click="removeKnowledge(item)" />
              </div>
            </div>
          </div>
          <div v-else-if="!knowledgeLoading" class="ai-chat-settings-page__small">{{ $t("ai_assistant.knowledge_empty") }}</div>
        </div>

        <div v-if="errorText" class="ai-chat-settings-page__error">{{ errorText }}</div>

        <div class="ai-chat-settings-page__actions">
          <DxButton
            styling-mode="contained"
            type="default"
            :text="loading ? $t('ai_assistant.settings_saving') : $t('common.save')"
            :disabled="loading || !canEdit || !canSubmit"
            @click="saveSettings"
          />
        </div>
      </div>
    </card>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { canEdit, canView, permissions, permissionTypes } from "@/helpers/Permissions";
import { errorMessage, successMessage } from "@/helpers/common";
import {
  applyModelVariantToModelId,
  describeAiChatError,
  deleteAiKnowledge,
  exportAiKnowledge,
  formatOpenRouterPrice,
  getAiChatSettings,
  importAiKnowledge,
  listAiKnowledge,
  saveAiKnowledge,
  getAiKnowledge,
  getOpenRouterModels,
  isAiChatNotConfiguredError,
  normalizeModelVariant,
  normalizeToolLoadingModeSetting,
  normalizeToolSearchMaxResultsSetting,
  OPENROUTER_MODEL_VARIANTS,
  OPENROUTER_TOOL_LOADING_MODES,
  resetAiChatCache,
  translateAiChatNotConfiguredReason,
  updateAiChatSettings,
} from "@/helpers/ai/openRouterMcpChat";

export default {
  name: "AiChatSettings",
  components: {
    DxButton,
    Forbidden,
  },
  data() {
    return {
      config: {
        permission: permissions.SETTINGS,
        permission_type: permissionTypes.module,
      },
      loading: false,
      cacheResetLoading: false,
      modelsLoading: false,
      errorText: "",
      modelsNoticeText: "",
      modelsNoticeReason: "",
      knowledgeLoading: false,
      knowledgeSaving: false,
      knowledgeImporting: false,
      knowledgeItems: [],
      knowledgeEditorOpen: false,
      editingKnowledgeId: null,
      knowledgeForm: { title: "", description: "" },
      currentSettings: {
        openrouter_api_url: "",
        openrouter_model: "",
        has_api_key: false,
        api_key_masked: "",
        system_prompt: "",
        initial_user_prompt: "",
        tool_calls_enabled: true,
        persist_loaded_tools_in_session: true,
        show_status_thinking: true,
        show_reasoning_thinking: true,
        show_tool_activity: true,
        show_token_costs: true,
        stream_reasoning_response: false,
        mcp_api_max_limit: 10,
        model_variant: "none",
        model_variants: [],
        built_in_tools_config: {},
        tool_method_confirmation: {},
        built_in_tools_catalog: [],
        assistant_temperature: 0.2,
        assistant_max_output_tokens: 8192,
        assistant_model_params: {},
        assistant_reasoning_enabled: false,
        assistant_reasoning_effort: "",
        assistant_include_reasoning: false,
        router_enabled: true,
        router_model: "",
        router_temperature: 0.1,
        router_max_output_tokens: 4096,
        router_model_params: {},
        router_reasoning_enabled: false,
        router_reasoning_effort: "",
        router_include_reasoning: false,
        tool_loading_mode: "router",
        tool_loading_modes: ["router", "all", "openrouter_tool_search"],
        openrouter_tool_search_max_results: 5,
        openrouter_response_cache_enabled: true,
        openrouter_response_cache_ttl_seconds: 3600,
        openrouter_cache_clear_pending: false,
      },
      form: {
        openrouter_api_url: "",
        openrouter_model: "",
        openrouter_api_key: "",
        clear_api_key: false,
        system_prompt: "",
        initial_user_prompt: "",
        tool_calls_enabled: true,
        persist_loaded_tools_in_session: true,
        show_status_thinking: true,
        show_reasoning_thinking: true,
        show_tool_activity: true,
        show_token_costs: true,
        stream_reasoning_response: false,
        mcp_api_max_limit: 10,
        model_variant: "none",
        built_in_tools_config: {},
        tool_method_confirmation: {},
        assistant_temperature: 0.2,
        assistant_max_output_tokens: 8192,
        assistant_model_params_json: "{}",
        assistant_reasoning_enabled: false,
        assistant_reasoning_effort: "",
        assistant_include_reasoning: false,
        router_enabled: true,
        router_model: "",
        router_temperature: 0.1,
        router_max_output_tokens: 4096,
        router_model_params_json: "{}",
        router_reasoning_enabled: false,
        router_reasoning_effort: "",
        router_include_reasoning: false,
        tool_loading_mode: "router",
        openrouter_tool_search_max_results: 5,
        openrouter_response_cache_enabled: true,
        openrouter_response_cache_ttl_seconds: 3600,
      },
      modelGroups: [],
      modelMap: {},
    };
  },
  computed: {
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    isSuperAdmin() {
      return Boolean(this.$store.getters["auth/user"]?.is_superadmin === 1);
    },
    canSubmit() {
      return Boolean(this.form.openrouter_api_url);
    },
    selectedModel() {
      return this.modelMap[this.form.openrouter_model] || null;
    },
    modelVariantList() {
      const fromSettings = Array.isArray(this.currentSettings.model_variants)
        ? this.currentSettings.model_variants
        : [];

      return fromSettings.length > 0 ? fromSettings : OPENROUTER_MODEL_VARIANTS;
    },
    effectiveModelId() {
      return applyModelVariantToModelId(this.form.openrouter_model, this.form.model_variant);
    },
    effectiveRouterModelId() {
      return applyModelVariantToModelId(this.form.router_model, this.form.model_variant);
    },
    selectedModelPricingText() {
      const pricing = this.selectedModel?.pricing;
      if (!pricing) {
        return "";
      }

      const promptPrice = formatOpenRouterPrice(pricing.prompt ?? pricing.input, "token");
      const completionPrice = formatOpenRouterPrice(pricing.completion ?? pricing.output, "token");
      return [
        promptPrice ? `${this.$t("ai_assistant.price_prompt")}: ${promptPrice}` : null,
        completionPrice ? `${this.$t("ai_assistant.price_completion")}: ${completionPrice}` : null,
      ].filter(Boolean).join(" | ");
    },
    apiKeyPlaceholder() {
      return this.currentSettings.has_api_key
        ? this.$t("ai_assistant.settings_api_key_keep_placeholder")
        : this.$t("ai_assistant.settings_api_key_placeholder");
    },
    builtInToolsCatalog() {
      const fromSettings = Array.isArray(this.currentSettings.built_in_tools_catalog)
        ? this.currentSettings.built_in_tools_catalog
        : [];

      if (fromSettings.length > 0) {
        return fromSettings;
      }

      return [
        { name: "render_html_canvas", title: "Render HTML Canvas", description: "Render HTML preview in chat." },
        { name: "render_devextreme_grid", title: "Render DevExtreme Grid", description: "Render tabular data preview." },
        { name: "get_current_datetime", title: "Get Current DateTime", description: "Return local date/time." },
        { name: "math_calculator", title: "Math Calculator", description: "Evaluate math expressions." },
        { name: "mcp_relevant_tools_index", title: "Relevant MCP Tools Index", description: "Return relevant MCP tools for request." },
        { name: "knowledge_base_search", title: "Knowledge Base Search", description: "Search titles in the AI knowledge base." },
        { name: "knowledge_base_read", title: "Knowledge Base Read", description: "Read one page from a knowledge item." },
      ];
    },
    jsonPlaceholder() {
      return '{\n  "top_p": 0.9\n}';
    },
    toolMethodList() {
      return ["get", "post", "put", "patch", "delete", "insert", "head", "options"];
    },
  },
  watch: {
    "$i18n.locale"() {
      // Keep the inline setup hint in sync with the newly selected language.
      if (this.modelsNoticeText) {
        this.modelsNoticeText = translateAiChatNotConfiguredReason(this.modelsNoticeReason);
      }
    },
  },
  async mounted() {
    if (!this.canView || !this.isSuperAdmin) {
      return;
    }

    await this.loadSettings();
    await this.loadModels();
    await this.loadKnowledge();
  },
  methods: {
    async loadSettings(forceRefresh = false) {
      this.errorText = "";
      try {
        const settings = await getAiChatSettings({ forceRefresh });
        this.currentSettings = {
          openrouter_api_url: settings.openrouter_api_url || "",
          openrouter_model: settings.openrouter_model || "",
          has_api_key: Boolean(settings.has_api_key),
          api_key_masked: settings.api_key_masked || "",
          system_prompt: settings.system_prompt || "",
          initial_user_prompt: settings.initial_user_prompt || "",
          tool_calls_enabled: settings.tool_calls_enabled !== false,
          persist_loaded_tools_in_session: settings.persist_loaded_tools_in_session !== false,
          show_status_thinking: settings.show_status_thinking !== false,
          show_reasoning_thinking: settings.show_reasoning_thinking !== false,
          show_tool_activity: settings.show_tool_activity !== false,
          show_token_costs: settings.show_token_costs !== false,
          stream_reasoning_response: Boolean(settings.stream_reasoning_response),
          mcp_api_max_limit: this.normalizeMcpApiMaxLimit(settings.mcp_api_max_limit),
          model_variant: normalizeModelVariant(settings.model_variant),
          model_variants: Array.isArray(settings.model_variants) ? settings.model_variants : [],
          tool_loading_mode: normalizeToolLoadingModeSetting(settings.tool_loading_mode),
          tool_loading_modes: Array.isArray(settings.tool_loading_modes) && settings.tool_loading_modes.length > 0
            ? settings.tool_loading_modes
            : OPENROUTER_TOOL_LOADING_MODES,
          openrouter_tool_search_max_results: normalizeToolSearchMaxResultsSetting(settings.openrouter_tool_search_max_results),
          built_in_tools_config: this.normalizeBuiltInToolsConfig(settings.built_in_tools_config),
          tool_method_confirmation: this.normalizeToolMethodConfirmation(settings.tool_method_confirmation),
          built_in_tools_catalog: Array.isArray(settings.built_in_tools_catalog) ? settings.built_in_tools_catalog : [],
          assistant_temperature: Number.isFinite(Number(settings.assistant_temperature)) ? Number(settings.assistant_temperature) : 0.2,
          assistant_max_output_tokens: Number.isFinite(Number(settings.assistant_max_output_tokens)) ? Number(settings.assistant_max_output_tokens) : 8192,
          assistant_model_params: settings.assistant_model_params && typeof settings.assistant_model_params === "object"
            ? settings.assistant_model_params
            : {},
          assistant_reasoning_enabled: Boolean(settings.assistant_reasoning_enabled),
          assistant_reasoning_effort: typeof settings.assistant_reasoning_effort === "string" ? settings.assistant_reasoning_effort : "",
          assistant_include_reasoning: Boolean(settings.assistant_include_reasoning),
          router_enabled: settings.router_enabled !== false,
          router_model: settings.router_model || settings.openrouter_model || "",
          router_temperature: Number.isFinite(Number(settings.router_temperature)) ? Number(settings.router_temperature) : 0.1,
          router_max_output_tokens: Number.isFinite(Number(settings.router_max_output_tokens)) ? Number(settings.router_max_output_tokens) : 4096,
          router_model_params: settings.router_model_params && typeof settings.router_model_params === "object"
            ? settings.router_model_params
            : {},
          router_reasoning_enabled: Boolean(settings.router_reasoning_enabled),
          router_reasoning_effort: typeof settings.router_reasoning_effort === "string" ? settings.router_reasoning_effort : "",
          router_include_reasoning: Boolean(settings.router_include_reasoning),
          openrouter_response_cache_enabled: settings.openrouter_response_cache_enabled !== false,
          openrouter_response_cache_ttl_seconds: this.normalizeCacheTtlSeconds(settings.openrouter_response_cache_ttl_seconds),
          openrouter_cache_clear_pending: Boolean(settings.openrouter_cache_clear_pending),
        };
        this.form = {
          openrouter_api_url: this.currentSettings.openrouter_api_url,
          openrouter_model: this.currentSettings.openrouter_model,
          openrouter_api_key: "",
          clear_api_key: false,
          system_prompt: this.currentSettings.system_prompt,
          initial_user_prompt: this.currentSettings.initial_user_prompt,
          tool_calls_enabled: this.currentSettings.tool_calls_enabled,
          persist_loaded_tools_in_session: this.currentSettings.persist_loaded_tools_in_session,
          show_status_thinking: this.currentSettings.show_status_thinking,
          show_reasoning_thinking: this.currentSettings.show_reasoning_thinking,
          show_tool_activity: this.currentSettings.show_tool_activity,
          show_token_costs: this.currentSettings.show_token_costs,
          stream_reasoning_response: this.currentSettings.stream_reasoning_response,
          mcp_api_max_limit: this.currentSettings.mcp_api_max_limit,
          model_variant: this.currentSettings.model_variant,
          tool_loading_mode: this.currentSettings.tool_loading_mode,
          openrouter_tool_search_max_results: this.currentSettings.openrouter_tool_search_max_results,
          built_in_tools_config: this.normalizeBuiltInToolsConfig(this.currentSettings.built_in_tools_config),
          tool_method_confirmation: this.normalizeToolMethodConfirmation(this.currentSettings.tool_method_confirmation),
          assistant_temperature: this.currentSettings.assistant_temperature,
          assistant_max_output_tokens: this.currentSettings.assistant_max_output_tokens,
          assistant_model_params_json: JSON.stringify(this.currentSettings.assistant_model_params || {}, null, 2),
          assistant_reasoning_enabled: this.currentSettings.assistant_reasoning_enabled,
          assistant_reasoning_effort: this.currentSettings.assistant_reasoning_effort,
          assistant_include_reasoning: this.currentSettings.assistant_include_reasoning,
          router_enabled: this.currentSettings.router_enabled,
          router_model: this.currentSettings.router_model,
          router_temperature: this.currentSettings.router_temperature,
          router_max_output_tokens: this.currentSettings.router_max_output_tokens,
          router_model_params_json: JSON.stringify(this.currentSettings.router_model_params || {}, null, 2),
          router_reasoning_enabled: this.currentSettings.router_reasoning_enabled,
          router_reasoning_effort: this.currentSettings.router_reasoning_effort,
          router_include_reasoning: this.currentSettings.router_include_reasoning,
          openrouter_response_cache_enabled: this.currentSettings.openrouter_response_cache_enabled,
          openrouter_response_cache_ttl_seconds: this.normalizeCacheTtlSeconds(this.currentSettings.openrouter_response_cache_ttl_seconds),
        };
      } catch (error) {
        this.errorText = describeAiChatError(error);
      }
    },
    async loadModels(forceRefresh = false) {
      this.modelsLoading = true;
      this.errorText = "";
      this.modelsNoticeText = "";
      this.modelsNoticeReason = "";

      try {
        const result = await getOpenRouterModels({ forceRefresh });
        this.modelGroups = Array.isArray(result?.grouped) ? result.grouped : [];

        const nextMap = {};
        this.modelGroups.forEach((group) => {
          (group.models || []).forEach((model) => {
            nextMap[model.id] = model;
          });
        });
        this.modelMap = nextMap;

        // No API key saved yet / rejected: the model list stays empty on purpose,
        // so explain it inline instead of showing a scary generic error.
        if (result?.notConfigured === true) {
          this.modelsNoticeReason = result?.reason || "settings_not_configured";
          this.modelsNoticeText = result?.message || translateAiChatNotConfiguredReason(this.modelsNoticeReason);
        }

        if (!this.form.openrouter_model && this.modelGroups[0]?.models?.[0]?.id) {
          this.form.openrouter_model = this.modelGroups[0].models[0].id;
        }
      } catch (error) {
        this.modelGroups = [];
        this.modelMap = {};

        if (isAiChatNotConfiguredError(error)) {
          this.modelsNoticeReason = error?.reason || "settings_not_configured";
          this.modelsNoticeText = describeAiChatError(error);
        } else {
          this.errorText = describeAiChatError(error);
        }
      } finally {
        this.modelsLoading = false;
      }
    },
    refreshModels() {
      this.loadModels(true);
    },
    async loadKnowledge() {
      this.knowledgeLoading = true;
      try {
        this.knowledgeItems = await listAiKnowledge();
      } catch (error) {
        this.errorText = describeAiChatError(error);
      } finally {
        this.knowledgeLoading = false;
      }
    },
    startNewKnowledge() {
      this.editingKnowledgeId = null;
      this.knowledgeForm = { title: "", description: "" };
      this.knowledgeEditorOpen = true;
    },
    async editKnowledge(item) {
      this.knowledgeLoading = true;
      this.errorText = "";
      try {
        const data = await getAiKnowledge(item.id);
        this.editingKnowledgeId = item.id;
        this.knowledgeForm = {
          title: data.title || "",
          description: data.description || "",
        };
        this.knowledgeEditorOpen = true;
      } catch (error) {
        this.errorText = describeAiChatError(error);
      } finally {
        this.knowledgeLoading = false;
      }
    },
    cancelKnowledgeEdit() {
      this.knowledgeEditorOpen = false;
      this.editingKnowledgeId = null;
      this.knowledgeForm = { title: "", description: "" };
    },
    async saveKnowledge() {
      if (!this.canEdit || !this.knowledgeForm.title || !this.knowledgeForm.description.trim()) {
        return;
      }
      this.knowledgeSaving = true;
      this.errorText = "";
      try {
        await saveAiKnowledge(this.knowledgeForm, this.editingKnowledgeId);
        this.cancelKnowledgeEdit();
        await this.loadKnowledge();
        successMessage(this, this.$t("ai_assistant.knowledge_saved"));
      } catch (error) {
        this.errorText = describeAiChatError(error);
        errorMessage(this, this.errorText);
      } finally {
        this.knowledgeSaving = false;
      }
    },
    async removeKnowledge(item) {
      if (!this.canEdit || !window.confirm(this.$t("ai_assistant.knowledge_delete_confirm", { title: item.title }))) {
        return;
      }
      this.knowledgeLoading = true;
      try {
        await deleteAiKnowledge(item.id);
        await this.loadKnowledge();
        successMessage(this, this.$t("ai_assistant.knowledge_deleted"));
      } catch (error) {
        this.errorText = describeAiChatError(error);
        errorMessage(this, this.errorText);
      } finally {
        this.knowledgeLoading = false;
      }
    },
    async exportKnowledge(uuid = null) {
      try {
        const data = await exportAiKnowledge(uuid);
        const fileName = uuid ? `ai-knowledge-${uuid}.json` : "ai-knowledge.json";
        const objectUrl = window.URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        this.errorText = describeAiChatError(error);
      }
    },
    async importKnowledgeFile(event) {
      const file = event?.target?.files?.[0];
      if (!file) {
        return;
      }
      this.knowledgeImporting = true;
      this.errorText = "";
      try {
        const parsed = JSON.parse(await file.text());
        const payload = Array.isArray(parsed)
          ? { format: "agrocontrol-ai-knowledge", version: 1, items: parsed }
          : parsed;
        if (!payload || payload.format !== "agrocontrol-ai-knowledge" || !Array.isArray(payload.items)) {
          throw new Error(this.$t("ai_assistant.knowledge_invalid_file"));
        }
        await importAiKnowledge(payload);
        await this.loadKnowledge();
        successMessage(this, this.$t("ai_assistant.knowledge_imported"));
      } catch (error) {
        this.errorText = error?.response ? describeAiChatError(error) : String(error?.message || error);
        errorMessage(this, this.errorText);
      } finally {
        this.knowledgeImporting = false;
        event.target.value = "";
      }
    },
    normalizeBuiltInToolsConfig(value) {
      const result = value && typeof value === "object" ? { ...value } : {};

      this.builtInToolsCatalog.forEach((tool) => {
        if (!result[tool.name] || typeof result[tool.name] !== "object") {
          result[tool.name] = { enabled: true, always_send: false };
          return;
        }

        result[tool.name] = {
          enabled: result[tool.name].enabled !== false,
          always_send: Boolean(result[tool.name].always_send),
        };
      });

      return result;
    },
    updateBuiltInToolFlag(toolName, field, checked) {
      const nextConfig = this.normalizeBuiltInToolsConfig(this.form.built_in_tools_config);
      if (!nextConfig[toolName]) {
        nextConfig[toolName] = { enabled: true, always_send: false };
      }
      nextConfig[toolName][field] = Boolean(checked);

      if (field === "enabled" && !checked) {
        nextConfig[toolName].always_send = false;
      }

      this.form.built_in_tools_config = nextConfig;
    },
    normalizeToolMethodConfirmation(value) {
      const defaults = {
        get: false,
        post: true,
        put: true,
        patch: true,
        delete: true,
        insert: true,
        head: false,
        options: false,
      };

      const source = value && typeof value === "object" ? value : {};
      Object.keys(defaults).forEach((method) => {
        if (Object.prototype.hasOwnProperty.call(source, method)) {
          defaults[method] = Boolean(source[method]);
        }
      });

      return defaults;
    },
    updateToolMethodConfirmation(method, checked) {
      const next = this.normalizeToolMethodConfirmation(this.form.tool_method_confirmation);
      next[method] = Boolean(checked);
      this.form.tool_method_confirmation = next;
    },
    parseJsonObjectOrEmpty(rawText, fieldLabel) {
      const text = String(rawText || "").trim();
      if (!text) {
        return {};
      }

      try {
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("invalid-json-object");
        }
        return parsed;
      } catch (error) {
        throw new Error(`${this.$t("ai_assistant.settings_invalid_json")}: ${fieldLabel}`);
      }
    },
    normalizeCacheTtlSeconds(value) {
      const fallback = 3600;
      const parsed = Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback;
      return Math.min(86400, Math.max(1, parsed));
    },
    normalizeMcpApiMaxLimit(value) {
      const fallback = 10;
      const parsed = Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback;
      return Math.min(1000, Math.max(1, parsed));
    },
    normalizeToolSearchMaxResults(value) {
      return normalizeToolSearchMaxResultsSetting(value);
    },
    async resetCache() {
      if (!this.canEdit) {
        return;
      }

      this.cacheResetLoading = true;
      this.errorText = "";

      try {
        await resetAiChatCache();
        await this.loadSettings(true);
        successMessage(this, this.$t("ai_assistant.settings_openrouter_cache_reset_success"));
      } catch (error) {
        this.errorText = describeAiChatError(error);
        errorMessage(this, this.errorText);
      } finally {
        this.cacheResetLoading = false;
      }
    },
    async saveSettings() {
      if (!this.canEdit || !this.canSubmit) {
        return;
      }

      this.loading = true;
      this.errorText = "";

      try {
        const assistantModelParams = this.parseJsonObjectOrEmpty(
          this.form.assistant_model_params_json,
          this.$t("ai_assistant.settings_assistant_model_params")
        );
        const routerModelParams = this.parseJsonObjectOrEmpty(
          this.form.router_model_params_json,
          this.$t("ai_assistant.settings_router_model_params")
        );

        const data = await updateAiChatSettings({
          openrouter_api_url: this.form.openrouter_api_url,
          openrouter_model: this.form.openrouter_model,
          openrouter_api_key: this.form.openrouter_api_key,
          clear_api_key: this.form.clear_api_key,
          system_prompt: this.form.system_prompt,
          initial_user_prompt: this.form.initial_user_prompt,
          tool_calls_enabled: this.form.tool_calls_enabled,
          persist_loaded_tools_in_session: this.form.persist_loaded_tools_in_session,
          show_status_thinking: this.form.show_status_thinking,
          show_reasoning_thinking: this.form.show_reasoning_thinking,
          show_tool_activity: this.form.show_tool_activity,
          show_token_costs: this.form.show_token_costs,
          stream_reasoning_response: this.form.stream_reasoning_response,
          mcp_api_max_limit: this.normalizeMcpApiMaxLimit(this.form.mcp_api_max_limit),
          model_variant: normalizeModelVariant(this.form.model_variant),
          tool_loading_mode: this.form.tool_loading_mode,
          openrouter_tool_search_max_results: this.normalizeToolSearchMaxResults(this.form.openrouter_tool_search_max_results),
          built_in_tools_config: this.normalizeBuiltInToolsConfig(this.form.built_in_tools_config),
          tool_method_confirmation: this.normalizeToolMethodConfirmation(this.form.tool_method_confirmation),
          assistant_temperature: this.form.assistant_temperature,
          assistant_max_output_tokens: this.form.assistant_max_output_tokens,
          assistant_model_params: assistantModelParams,
          assistant_reasoning_enabled: this.form.assistant_reasoning_enabled,
          assistant_reasoning_effort: this.form.assistant_reasoning_effort || null,
          assistant_include_reasoning: this.form.assistant_include_reasoning,
          router_enabled: this.form.router_enabled,
          router_model: this.form.router_model,
          router_temperature: this.form.router_temperature,
          router_max_output_tokens: this.form.router_max_output_tokens,
          router_model_params: routerModelParams,
          router_reasoning_enabled: this.form.router_reasoning_enabled,
          router_reasoning_effort: this.form.router_reasoning_effort || null,
          router_include_reasoning: this.form.router_include_reasoning,
          openrouter_response_cache_enabled: this.form.openrouter_response_cache_enabled,
          openrouter_response_cache_ttl_seconds: this.normalizeCacheTtlSeconds(this.form.openrouter_response_cache_ttl_seconds),
        });

        this.currentSettings = {
          openrouter_api_url: data.openrouter_api_url || this.form.openrouter_api_url,
          openrouter_model: data.openrouter_model || this.form.openrouter_model,
          has_api_key: Boolean(data.has_api_key),
          api_key_masked: data.api_key_masked || "",
          system_prompt: data.system_prompt || this.form.system_prompt,
          initial_user_prompt: data.initial_user_prompt || this.form.initial_user_prompt,
          tool_calls_enabled: data.tool_calls_enabled !== false,
          persist_loaded_tools_in_session: data.persist_loaded_tools_in_session !== false,
          show_status_thinking: data.show_status_thinking !== false,
          show_reasoning_thinking: data.show_reasoning_thinking !== false,
          show_tool_activity: data.show_tool_activity !== false,
          show_token_costs: data.show_token_costs !== false,
          stream_reasoning_response: Boolean(data.stream_reasoning_response),
          mcp_api_max_limit: this.normalizeMcpApiMaxLimit(data.mcp_api_max_limit),
          model_variant: normalizeModelVariant(data.model_variant),
          tool_loading_mode: normalizeToolLoadingModeSetting(data.tool_loading_mode),
          tool_loading_modes: Array.isArray(data.tool_loading_modes) && data.tool_loading_modes.length > 0
            ? data.tool_loading_modes
            : OPENROUTER_TOOL_LOADING_MODES,
          openrouter_tool_search_max_results: normalizeToolSearchMaxResultsSetting(
            data.openrouter_tool_search_max_results ?? this.form.openrouter_tool_search_max_results
          ),
          built_in_tools_config: this.normalizeBuiltInToolsConfig(data.built_in_tools_config),
          tool_method_confirmation: this.normalizeToolMethodConfirmation(data.tool_method_confirmation),
          built_in_tools_catalog: Array.isArray(data.built_in_tools_catalog) ? data.built_in_tools_catalog : this.currentSettings.built_in_tools_catalog,
          assistant_temperature: Number.isFinite(Number(data.assistant_temperature)) ? Number(data.assistant_temperature) : this.form.assistant_temperature,
          assistant_max_output_tokens: Number.isFinite(Number(data.assistant_max_output_tokens)) ? Number(data.assistant_max_output_tokens) : this.form.assistant_max_output_tokens,
          assistant_model_params: data.assistant_model_params && typeof data.assistant_model_params === "object"
            ? data.assistant_model_params
            : assistantModelParams,
          assistant_reasoning_enabled: Boolean(data.assistant_reasoning_enabled),
          assistant_reasoning_effort: typeof data.assistant_reasoning_effort === "string" ? data.assistant_reasoning_effort : (this.form.assistant_reasoning_effort || ""),
          assistant_include_reasoning: Boolean(data.assistant_include_reasoning),
          router_enabled: data.router_enabled !== false,
          router_model: data.router_model || this.form.router_model,
          router_temperature: Number.isFinite(Number(data.router_temperature)) ? Number(data.router_temperature) : this.form.router_temperature,
          router_max_output_tokens: Number.isFinite(Number(data.router_max_output_tokens)) ? Number(data.router_max_output_tokens) : this.form.router_max_output_tokens,
          router_model_params: data.router_model_params && typeof data.router_model_params === "object"
            ? data.router_model_params
            : routerModelParams,
          router_reasoning_enabled: Boolean(data.router_reasoning_enabled),
          router_reasoning_effort: typeof data.router_reasoning_effort === "string" ? data.router_reasoning_effort : (this.form.router_reasoning_effort || ""),
          router_include_reasoning: Boolean(data.router_include_reasoning),
          openrouter_response_cache_enabled: data.openrouter_response_cache_enabled !== false,
          openrouter_response_cache_ttl_seconds: this.normalizeCacheTtlSeconds(data.openrouter_response_cache_ttl_seconds),
          openrouter_cache_clear_pending: Boolean(data.openrouter_cache_clear_pending),
        };
        this.form.openrouter_model = this.currentSettings.openrouter_model || this.form.openrouter_model;
        this.form.router_model = this.currentSettings.router_model || this.form.router_model;
        this.form.openrouter_api_key = "";
        this.form.clear_api_key = false;
        this.form.built_in_tools_config = this.normalizeBuiltInToolsConfig(this.currentSettings.built_in_tools_config);
        this.form.tool_method_confirmation = this.normalizeToolMethodConfirmation(this.currentSettings.tool_method_confirmation);
        this.form.assistant_model_params_json = JSON.stringify(this.currentSettings.assistant_model_params || {}, null, 2);
        this.form.router_model_params_json = JSON.stringify(this.currentSettings.router_model_params || {}, null, 2);
        this.form.openrouter_response_cache_enabled = this.currentSettings.openrouter_response_cache_enabled;
        this.form.openrouter_response_cache_ttl_seconds = this.normalizeCacheTtlSeconds(this.currentSettings.openrouter_response_cache_ttl_seconds);
        this.form.show_token_costs = this.currentSettings.show_token_costs;
        this.form.mcp_api_max_limit = this.currentSettings.mcp_api_max_limit;
        this.form.model_variant = this.currentSettings.model_variant;
        this.form.tool_loading_mode = this.currentSettings.tool_loading_mode;
        this.form.openrouter_tool_search_max_results = this.currentSettings.openrouter_tool_search_max_results;
        // Reload the model list so the new API key is validated immediately.
        await this.loadModels(true);
        successMessage(this, this.$t("ai_assistant.settings_saved"));
      } catch (error) {
        this.errorText = describeAiChatError(error);
        errorMessage(this, this.errorText);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ai-chat-settings-page__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-chat-settings-page__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-chat-settings-page__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fcfcfd;
}

.ai-chat-settings-page__section-title {
  color: #111827;
  font-size: 14px;
  font-weight: 700;
}

.ai-chat-settings-page__subsection-title {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
}

.ai-chat-settings-page__method-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.ai-chat-settings-page__method-item {
  min-height: 34px;
}

.ai-chat-settings-page__grid-two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.ai-chat-settings-page__label-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.ai-chat-settings-page__label {
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
}

.ai-chat-settings-page__input {
  width: 100%;
  min-height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.ai-chat-settings-page__textarea {
  width: 100%;
  min-height: 120px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  resize: vertical;
}

.ai-chat-settings-page__small {
  color: #6b7280;
  font-size: 12px;
}

.ai-chat-settings-page__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
}

.ai-chat-settings-page__checkbox--inline {
  min-height: 40px;
}

.ai-chat-settings-page__tool-list {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.ai-chat-settings-page__tool-header,
.ai-chat-settings-page__tool-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 140px;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
}

.ai-chat-settings-page__tool-header {
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
}

.ai-chat-settings-page__tool-row {
  border-top: 1px solid #eef2f7;
}

.ai-chat-settings-page__tool-name {
  color: #111827;
  font-size: 13px;
  font-weight: 600;
}

.ai-chat-settings-page__model-card {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.ai-chat-settings-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.ai-chat-settings-page__file-input {
  display: none;
}

.ai-chat-settings-page__knowledge-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
}

.ai-chat-settings-page__knowledge-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e5e7eb;
}

.ai-chat-settings-page__knowledge-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.ai-chat-settings-page__knowledge-meta {
  min-width: 0;
  overflow-wrap: anywhere;
}

.ai-chat-settings-page__error {
  color: #b42318;
  font-size: 13px;
}

.ai-chat-settings-page__notice {
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid #f2c57c;
  border-left: 4px solid #e8a33d;
  border-radius: 8px;
  background-color: #fff8ec;
  color: #6b4a12;
  font-size: 13px;
  line-height: 1.45;
}

@media (max-width: 900px) {
  .ai-chat-settings-page__grid-two {
    grid-template-columns: minmax(0, 1fr);
  }

  .ai-chat-settings-page__method-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ai-chat-settings-page__tool-header,
  .ai-chat-settings-page__tool-row {
    grid-template-columns: minmax(0, 1fr) 100px 120px;
  }

  .ai-chat-settings-page__knowledge-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .ai-chat-settings-page__knowledge-row .ai-chat-settings-page__actions {
    justify-content: flex-start;
  }
}
</style>